import forge from 'apis/forge';
import DsConversation from 'components/Panel/Chat/Conversation/DsConversation';
import { Loader } from 'components/Widget/Loader';
import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { ChatStatus } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveDesignSystem, selectActiveDsChat, selectDsChatRefs } from 'redux/db/reducers';
import { designSystemEditingBlocked } from 'redux/db/util';
import { setActiveDsChatId } from 'redux/mode/actions';
import { displayDsReadOnlyModal } from 'redux/mode/util/modals';
import { RootReducer } from 'redux/reducers';
import { v4 as uuid } from 'uuid';
import { Header } from '../Header';
import * as Base from '../styles';

const DsChatPanel = () => {
	const dispatch = useDispatch();

	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

	const {
		dsChatRefs,
		activeDsChatId,
		activeDesignSystem,
		activeChatTitle,
		chatsLoading,
		dsVersionLoaded,
	} = useSelector((r: RootReducer) => {
		const activeDsChatId = r.mode.activeDsChatId;
		const dsChatRefs = selectDsChatRefs(r.db);
		const activeChat = selectActiveDsChat(r.db, activeDsChatId);

		const activeChatIdInRefs = activeDsChatId
			? dsChatRefs.some((ref) => ref.id === activeDsChatId)
			: true;

		return {
			dsChatRefs,
			activeDsChatId,
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeChatTitle: activeChat ? activeChat.title || 'Untitled Chat' : '',
			chatsLoading: activeDsChatId !== null && !activeChatIdInRefs,
			dsVersionLoaded: r.db.loading.activeDesignSystemVersion === 'loaded',
		};
	}, shallowEqual);

	// Chat refs are already sorted by updatedAt DESC from the backend
	const sortedChatRefs = dsChatRefs;

	useEffect(() => {
		// Don't auto-select while a new chat is being created and hasn't appeared in chatRefs yet
		if (chatsLoading) return;

		if (!activeDsChatId || !sortedChatRefs.some((ref) => ref.id === activeDsChatId)) {
			const firstChatRef = sortedChatRefs.length > 0 ? sortedChatRefs[0] : null;
			if (firstChatRef) {
				dispatch(setActiveDsChatId(firstChatRef.id));
			}
		}
	}, [sortedChatRefs, activeDsChatId, dispatch, chatsLoading]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onNewChat = async () => {
		if (!activeDesignSystem) return;
		const blocked = designSystemEditingBlocked();
		if (blocked) {
			displayDsReadOnlyModal(blocked);
			return;
		}

		const chatId = uuid();
		const oldActiveDsChatId = activeDsChatId;

		try {
			await forge.createDsChat(
				{
					id: chatId,
					designSystemId: activeDesignSystem.id,
					title: NEW_CHAT_TITLE,
					metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Initializing chat...' },
				},
				true
			);
		} catch (e) {
			dispatch(setActiveDsChatId(oldActiveDsChatId));
			throw e;
		}
		// Intentionally set after the API call — setting it before caused the poll cache to send
		// requests for a chat that didn't exist yet. The chatsLoading guard in the useEffect above
		// prevents the auto-select logic from clobbering this while chatRefs catches up.
		dispatch(setActiveDsChatId(chatId));
	};

	const moreMenuItems = useMemo(() => {
		if (!activeDsChatId) return [];
		return [MenuItemAction.createRenameDsChat(activeDsChatId)];
	}, [activeDsChatId]);

	return (
		<Base.Base ref={ref}>
			<Header
				title={activeChatTitle}
				onNewChat={onNewChat}
				chats={sortedChatRefs}
				activeChatId={activeDsChatId}
				onSelectChat={(id: string) => dispatch(setActiveDsChatId(id))}
				moreMenuItems={moreMenuItems}
			/>
			{activeDsChatId ? (
				<Base.Conversation>
					<DsConversation />
				</Base.Conversation>
			) : activeDesignSystem && !dsVersionLoaded ? (
				<Base.LoadingContainer>
					<Loader width={24} height={24} />
				</Base.LoadingContainer>
			) : null}
		</Base.Base>
	);
};

export default DsChatPanel;
